import { DAY } from '@/sim/clock';
import {
  asked, becameOf, BECOMING_RECENT, companionOf, hauntOf, lossesOf, standsOutIn,
  type TraitAxis,
} from '@/sim/mortals/people';
import { socialCenter } from '@/sim/social/socialGrid';
import type { BandState, PartyState, PersonState } from '@/sim/mortals/types';
import type { HudPerson } from './hud';

/**
 * Putting one mortal into words (v0.3.2 §3, §51).
 *
 * The whole danger of a follow panel is that it becomes a management screen:
 * the moment a life is a row of numbers, the player stops watching people and
 * starts auditing labour, and the game is over. So nothing here renders a
 * quantity. Vitality becomes "worn", an aptitude becomes "a hunter's hands"
 * and never a level, and traits become the two or three words a neighbour
 * would actually use. A person is recognisable by what they repeatedly do,
 * which is a thing you notice by looking, not by reading a stat.
 *
 * Pure formatting over simulation state, called from the frame loop.
 */

/** Small counts read as words; past that a number is what a person would say. */
const COUNT = ['nobody', 'one other', 'two others', 'three others', 'four others',
               'five others', 'six others', 'seven others', 'eight others',
               'nine others', 'ten others', 'eleven others'] as const;
/**
 * Who they are with. Past a dozen nobody counts heads — they say how much of
 * the community it is, which is what a person would actually notice and
 * which keeps a numeral out of the panel. (A party of thirteen once put
 * "with 12 others" on screen and the no-numbers tripwire caught it, which is
 * exactly what that assertion is for.)
 */
const others = (n: number, band: number): string => {
  if (n <= 0) return 'alone';
  if (n < COUNT.length) return `with ${COUNT[n]}`;
  const share = n / Math.max(1, band - 1);
  return share > 0.8 ? 'with nearly everyone'
    : share > 0.5 ? 'with most of the band'
    : 'with a good many others';
};

const distance = (m: number): string =>
  m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`;

/** What the work is, in the world's voice rather than the scheduler's. */
const WORK: Record<string, string> = {
  camp: 'keeping the fire',
  idle: 'about the camp',
  rest: 'resting',
  build: 'working the frame',
  forage: 'out foraging',
  hunt: 'out after the herd',
  gather_wood: 'cutting wood',
  seek_water: 'fetching water',
  explore: 'away scouting',
  investigate: 'gone to where the sign pointed',
  venerate: 'gone back to the standing stone',
  travel: 'on the move with the band',
  flee: 'running',
};

/** How a memory was come by, in the panel's margin. */
const HOW: Record<string, string> = {
  direct: 'was there',
  observed: 'saw it',
  heard: 'heard it',
  told: 'was told',
};

/**
 * …and from WHOM, when the story came from another band (`PersonMemory.from`,
 * `BandSystem.meetStrangers`). This is the line that shows a consequence has
 * left the community it happened in — *lightning fell · heard from the
 * Corrwalkers* on a person the god never went near. Another camp of the same
 * people is said as such; a people the world can no longer find are
 * travellers, which is what a neighbour could honestly say.
 */
const heardFrom = (
  from: string, own: string | undefined,
  peopleOf?: (bandId: string) => string | undefined,
): string => {
  const name = peopleOf?.(from);
  return !name ? 'heard from travellers'
    : name === own ? 'heard from another camp'
      : `heard from the ${name}`;
};

/** How someone is holding up. Words, always — never a percentage. */
const condition = (vitality: number): string =>
  vitality > 0.88 ? 'hale'
  : vitality > 0.72 ? 'well enough'
  : vitality > 0.55 ? 'worn'
  : vitality > 0.35 ? 'ailing'
  : 'failing';

/**
 * The two or three words a neighbour would use. Only the leanings that stand
 * out get named: a person who is middling at everything is even-tempered, not
 * a list of averages.
 */
/**
 * THE CARD'S OWN LINES, per axis (2026-08-23, `tools/temper.ts`). The flat
 * `STANDS_OUT`/`STANDS_APART` pair (0.66/0.34) put "bold · a hunter's hands"
 * on 21.2% of the third century, because the population those lines were
 * measured against moved: courage p50 sits at 0.76 and hunt at 0.83 on this
 * island, so half of everyone was "bold" and 97.8% carried a hands word.
 * The card's word is for the unmistakable, so each axis gets lines set
 * against its own real distribution — measured over all 406 souls, rule C
 * of the sweep: 65 distinct phrases, the commonest on 10.6%, even-tempered
 * 9.9%, a hands word on 59.6% (a subsistence island's hands SHOULD be
 * common — just not universal).
 *
 * The SIM's lines are untouched: a becoming still crosses `people.ts`'s
 * 0.66/0.34, because a crossing is history at a fixed reference. *Has grown
 * bold* is the recent fact of crossing into boldness; plain *bold* is what
 * a neighbour calls the top of the camp. A person can honestly be the first
 * without yet being the second.
 */
const CARD_LINE = {
  courage: [0.86, 0.56],
  curiosity: [0.66, 0.42],
  sociability: [0.66, 0.18],
} as const;
/** The hands are an inclination, not a rank: the best aptitude must be good
 *  AND lead the second by a real margin, or the card says nothing. */
const HANDS_FLOOR = 0.7;
const HANDS_LEAD = 0.1;

export function temper(p: PersonState): string {
  const words: string[] = [];
  const t = p.traits;
  if (t.courage >= CARD_LINE.courage[0]) words.push('bold');
  else if (t.courage <= CARD_LINE.courage[1]) words.push('wary');
  if (t.curiosity >= CARD_LINE.curiosity[0]) words.push('curious');
  else if (t.curiosity <= CARD_LINE.curiosity[1]) words.push('settled');
  if (t.sociability >= CARD_LINE.sociability[0]) words.push('well liked');
  else if (t.sociability <= CARD_LINE.sociability[1]) words.push('keeps apart');
  // and the hands, if they are unmistakable — an inclination, not a rank
  const a = p.aptitudes;
  const ranked = (['hunt', 'forage', 'build', 'explore'] as const)
    .slice().sort((x, y) => a[y] - a[x]);
  if (a[ranked[0]] >= HANDS_FLOOR && a[ranked[0]] - a[ranked[1]] >= HANDS_LEAD) {
    words.push({ hunt: "a hunter's hands", forage: "a forager's eye",
                 build: "a builder's hands", explore: "a wanderer's feet" }[ranked[0]]);
  }
  return words.length ? words.join(' · ') : 'even-tempered';
}

/**
 * WHAT KIND OF PERSON THEY ARE BECOMING — `PLAYABLE.md` P2's own question,
 * which the axes alone cannot answer: they say who somebody IS. The most
 * recent line they crossed (`PersonState.became`), as a change rather than a
 * state, and only while it is recent enough to be a becoming rather than a
 * fact — a decade, which is long enough for a child who grew bold to be a
 * bold adult and short enough that *has grown* is still true. The words are
 * `temper()`'s own, conjugated.
 */
const BECAME: Record<TraitAxis, [up: string, down: string]> = {
  courage: ['has grown bold', 'has grown wary'],
  curiosity: ['has grown curious', 'has settled'],
  sociability: ['has come to be well liked', 'has come to keep apart'],
  hunt: ["has grown into a hunter's hands", "has lost the hunter's hands they had"],
  forage: ["has grown a forager's eye", "has lost the forager's eye they had"],
  build: ["has grown into a builder's hands", "has lost the builder's hands they had"],
  explore: ["has grown a wanderer's feet", "has lost the wanderer's feet they had"],
};
/**
 * …and how they stand against their own band, on the axis they stand
 * furthest on — `standsOutIn`, spoken for about half the island and silent
 * for the rest, who are ordinary among their own. The words a neighbour
 * would use; never a comparison to anyone by name.
 */
const STANDS: Record<TraitAxis, [up: string, down: string]> = {
  courage: ['bolder than most here', 'more wary than most here'],
  curiosity: ['more curious than most here', 'less curious than most here'],
  sociability: ['closer to the others than most here', 'keeps more to themselves than most here'],
  hunt: ['a surer hand at the hunt than most here', 'a poorer hand at the hunt than most here'],
  forage: ['a keener eye for forage than most here', 'a poorer eye for forage than most here'],
  build: ['handier at building than most here', 'less handy at building than most here'],
  explore: ['ranges further than most here', 'ranges less than most here'],
};
const stands = (p: PersonState, band: BandState): string | null => {
  const s = standsOutIn(p, band.people);
  return s ? STANDS[s.axis][s.up ? 0 : 1] : null;
};

/**
 * WHERE THEY KEEP GOING — `PLAYABLE.md` P2's *where do you go repeatedly*,
 * from `hauntOf`: the ground they have worked most, when it stands clear of
 * the others and has had its `HAUNT_SPEAKS` days. Said against the camp —
 * the direction a neighbour would point and how far they would call it — or
 * by a landmark's name when the caller can resolve one near the ground
 * (`placeOf`). Never a distance in metres: the card's wall.
 */
const COMPASS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];
const haunt = (
  p: PersonState, band: BandState, placeOf?: (x: number, z: number) => string | undefined,
): string | null => {
  const h = hauntOf(p);
  if (!h) return null;
  const [sx, sz] = h.cell.split(',').map(Number);
  if (!Number.isFinite(sx) || !Number.isFinite(sz)) return null;
  const c = socialCenter(sx, sz);
  const named = placeOf?.(c.x, c.z);
  if (named) return `works the ground by ${named}, most days`;
  const d = Math.hypot(c.x - band.homeX, c.z - band.homeZ);
  if (d < 600) return 'works the ground around the camp, most days';
  const bearing = (Math.atan2(c.x - band.homeX, -(c.z - band.homeZ)) * 180) / Math.PI;
  const dir = COMPASS[Math.round(((bearing + 360) % 360) / 45) % 8];
  const far = d < 1100 ? 'a short walk out' : d < 2200 ? 'a good walk out' : 'a long walk out';
  return `works the ground to the ${dir}, ${far}, most days`;
};

function becoming(p: PersonState, now: number): string | null {
  const b = becameOf(p)[0];
  if (!b || now - b.at > BECOMING_RECENT) return null;
  return BECAME[b.axis][b.up ? 0 : 1];
}

/**
 * HOW MUCH OF THE INEXPLICABLE THIS PERSON HAS PERSONALLY STOOD IN.
 *
 * `person.importance` has had **two writers and no readers** since it was
 * built. Both writers need a *divine* event witnessed at close range — so on
 * the shipped century, generated by a tool with no god in it, **every one of
 * the 464 souls sits at exactly zero.** Which is not a defect: it makes this
 * the one field on a person that is a record of the PLAYER, and the only way
 * it moves is by a god having been near them.
 *
 * That is why it earns a line the memory list does not already give. The
 * memories say *what* they carry, one thing at a time and newest first. This
 * says how much of a life has been spent inside things that had no
 * explanation — a weight rather than a list, and the difference between
 * somebody who once saw a strange light and somebody the strange keeps
 * happening to.
 *
 * **Law 10 holds and is the whole reason for the wording.** Not one of these
 * says a god, a hand, or a cause. `importance` is raised by witnessing a
 * divine event and the world's record of *whose* it was never leaves the
 * world; what a neighbour could say is that this person keeps being present
 * when the unaccountable happens, and that is exactly what is said.
 *
 * The bands are the writer's own units: a direct witness of a full-salience
 * act adds 0.15, an observed one 0.05, and walking to a sign that turned out
 * to hold something adds 0.12. So one close miracle clears the first band, and
 * the top band is a life that has had several.
 */
const marked = (importance: number): string | null =>
  importance >= 0.45 ? 'the unaccountable keeps happening where they are'
    : importance >= 0.20 ? 'has stood inside something nobody could explain'
      : importance >= 0.06 ? 'was near something nobody could explain, once'
        : null;

/** `describe().where` with its distance taken back out, for the card. */
const qualitativeWhere = (where: string): string => {
  const walking = where.includes('walking');
  const home = where.includes('at the camp');
  return walking
    ? (home ? 'walking, near the camp' : 'walking, out on the ground')
    : (home ? 'at the camp' : 'out on the ground');
};

/**
 * A person's readings, for the one card.
 *
 * The card is a trading card and a trading card is mostly numbers, which makes
 * this the most dangerous surface in the game to build: `test/follow.ts` exists
 * because the moment a life is a row of quantities the player stops watching
 * people and starts auditing labour. So a person's lines are assembled here,
 * out of the same phrases the life panel already used, and **nothing is
 * computed that could be a number**. `cardHTML` withholds any line that carries
 * a digit anyway; that is the wall, and this is the discipline that means the
 * wall never has to be hit.
 *
 * It returns the `now` block and nothing else. Identity, the seal, the verb
 * register, what they carry and what the ground remembers all come from the
 * same places they come from for a tree — which is the whole point of the card
 * being one shell rather than a panel per kind.
 */
export function personCard(p: HudPerson): string[] {
  return [
    p.doing,
    // `describe().where` carries a distance ("1.2 km from the camp"), which is
    // a fact about the GROUND and is why the life panel could print it. The
    // card cannot: the guard cannot tell a metre from a stat, and a rule that
    // fires on every ordinary person is a rule nobody reads. So the card keeps
    // the half of the phrase that is about the person.
    qualitativeWhere(p.where),
    `${p.condition} · ${p.temper}`,
    // …how they stand against their own people, when they stand clear of
    // them — the line that tells two hunters in one camp apart
    ...(p.stands ? [p.stands] : []),
    // …and what they are becoming, when they lately became anything — the
    // change behind the word above, which the word alone cannot carry
    ...(p.becoming ? [p.becoming] : []),
    // …and where they keep going, when one ground stands clear of the rest
    ...(p.haunt ? [p.haunt] : []),
    // `describe` falls back to the raw band id when a band has not diverged
    // into a people yet, and `band-1` is a database row wearing a digit.
    // Saying so plainly is better than printing the row — and better than
    // letting the guard withhold the line, which would leave a person with no
    // people at all rather than a person whose people have no name.
    /\d/.test(p.band) ? 'of a band with no name of its own' : p.band,
    /\d/.test(p.since) ? 'came to them later' : p.since,
    // …and who matters to them, when anybody does. Most people have no line
    // here, which is the truthful answer and not a hole: `companionOf` refuses
    // to name somebody who does not stand clear of the rest of the circle.
    ...p.ties,
    // …and what the world has already done to them, when it has done anything.
    // Null for almost everybody, which is the point: the first person a player
    // sees this on is somebody their own hand has been near.
    ...(p.marked ? [p.marked] : []),
  ];
}

/**
 * `followOrder` stood here: the order `F` walked a band's people, importance
 * first. It is gone with the key.
 *
 * The whole idea was that a god presses a key and arrives at somebody — which
 * ranks a community's lives for the player and hands them the top of the list.
 * A god should have to find a person by flying over the ground they are
 * standing on. **You cannot care about something you have never found**, and a
 * roster is the shortest possible way of never having found anything.
 *
 * What the sort knew is not lost, only relocated: `importance` still rises with
 * what history does to somebody, and it is what will decide who is worth a
 * name at a distance when the world starts offering them.
 */

/** Everything the card says about one life. */
/**
 * WHO MATTERS TO THIS PERSON — lineage and company, in words.
 *
 * `nameOf` has to reach across the whole world and not merely this band,
 * because fission is what makes a tie interesting: half a band walks four
 * kilometres away and somebody is still, every day, the person you used to
 * work beside. It returns `undefined` for the dead and for anybody the world
 * can no longer find, and **that case is not an error to be swallowed** — it
 * is the one the whole feature exists to deliver.
 *
 * Not one of these lines may carry a digit. `cardHTML` withholds any line that
 * does, and a rule that fires on ordinary people is a rule nobody reads — so
 * "parent of Mara and others" rather than a number of children, and no count
 * of days anywhere.
 */
function tiesOf(
  person: PersonState, band: BandState,
  nameOf?: (id: string) => string | undefined,
): string[] {
  if (!nameOf) return [];
  const out: string[] = [];
  // WHO THEY HAVE LOST — written by the death itself (`BandSystem.mourn`), so
  // the card can tell a death from a parting. `parentId` and `bonds` both
  // simply stop resolving either way; this is the only record that says which.
  const lost = lossesOf(person);
  const dead = (id: string) => lost.find((l) => l.id === id);

  // lineage, which has been in the data since DESIGN-VI and read by nothing
  if (person.parentId) {
    const n = nameOf(person.parentId);
    const d = dead(person.parentId);
    out.push(n ? `child of ${n}`
      : d ? `child of ${d.name}, who died`
        : 'their people are gone from here');
  }
  const kids = band.people.filter((q) => q.parentId === person.id);
  if (kids.length === 1) out.push(`parent of ${kids[0].name}`);
  else if (kids.length > 1) out.push(`parent of ${kids[0].name} and others`);
  const buried = lost.filter((l) => l.tie === 'child');
  if (buried.length === 1) out.push(`lost ${buried[0].name}, their child`);
  else if (buried.length > 1) out.push(`lost ${buried[0].name} and other children`);

  // and the company they keep, if anybody genuinely stands out
  const c = companionOf(person);
  if (c) {
    const n = nameOf(c.id);
    const d = dead(c.id);
    out.push(n
      ? `often out with ${n}`
      // The line that makes a death mean something — and now the world can
      // say WHICH loss it was. When the death is recorded, the card says so
      // by name. When it is not, it is deliberately not phrased as a
      // bereavement: they may have walked away with the other half of a split
      // or simply stopped being put on the same errands, and the honest line
      // is that the person they kept turning up beside is not here.
      : d ? `lost ${d.name}, who they worked beside`
        : 'the one they worked beside is not here now');
  } else {
    // a companion whose bond has faded since the death is still a loss: the
    // most recent one the card has not already said
    const gone = lost.find((l) => l.tie === 'companion');
    if (gone) out.push(`lost ${gone.name}, who they worked beside`);
  }
  return out;
}

/**
 * The seven axes, in the world's language rather than the simulation's.
 *
 * These are the ONLY seven a verb can reach — `TraitAxis` is a closed union
 * and `setAxis` is typed to it — so this table is exhaustive by construction.
 * Everything a UI might otherwise be tempted to offer about a person is
 * god-proof and stays off the card: `vitality` is never written after birth,
 * `importance` is written by witnessing alone, and `bonds` and `shown` are
 * written only by living. Naming any of them here would be inventing a lever
 * that does not exist.
 */
const TAKES: Record<TraitAxis, string> = {
  curiosity: 'their curiosity',
  courage: 'their nerve',
  sociability: 'their hold on the others',
  hunt: 'their hand at the hunt',
  forage: 'their eye for forage',
  build: 'their hand at building',
  explore: 'how far they will range',
};

export function describe(
  person: PersonState, band: BandState, party: PartyState | undefined,
  /** Which people they are (§50). A band id is a database row; this is not. */
  culture?: { name: string },
  /** Resolve any person id in the WORLD to a name — see `tiesOf`. */
  nameOf?: (id: string) => string | undefined,
  /** Resolve a band id to the name of its people — see `heardFrom`. */
  peopleOf?: (bandId: string) => string | undefined,
  /** World-seconds now, for *has grown bold* to mean lately. Absent: never said. */
  now?: number,
  /** A landmark's name near a point, for *works the ground by the Sweet Fork*. */
  placeOf?: (x: number, z: number) => string | undefined,
): HudPerson {
  const work = party ? WORK[party.activity] ?? 'about the camp' : 'apart from the others';
  const fromCamp = party
    ? Math.hypot(party.x - band.homeX, party.z - band.homeZ)
    : Math.hypot(band.x - band.homeX, band.z - band.homeZ);
  const walking = !!party?.target &&
    Math.hypot(party.target.x - party.x, party.target.z - party.z) > 25;
  const where = [
    walking ? 'walking' : null,
    fromCamp < 60 ? 'at the camp' : `${distance(fromCamp)} from the camp`,
  ].filter(Boolean).join(' · ');
  return {
    name: person.name,
    // who they are, not which record they live in
    band: culture ? `of the ${culture.name}` : band.id,
    since: person.bornAt <= band.foundedAt + DAY
      ? 'here from the first day'
      : `here since day ${Math.floor(person.bornAt / DAY)}`,
    doing: `${work} ${others((party?.size ?? 1) - 1, Math.round(band.population))}`,
    where,
    condition: condition(person.vitality),
    temper: temper(person),
    ties: tiesOf(person, band, nameOf),
    ...(marked(person.importance) ? { marked: marked(person.importance)! } : {}),
    ...(now !== undefined && becoming(person, now) ? { becoming: becoming(person, now)! } : {}),
    ...(stands(person, band) ? { stands: stands(person, band)! } : {}),
    ...(haunt(person, band, placeOf) ? { haunt: haunt(person, band, placeOf)! } : {}),
    // `asked` is the same mapping `press`, `suitability` and `liveADay` all
    // read, so what the card names is exactly what a cast would move — one
    // source, not a copy that can drift. Order is the weight order `WORK`
    // declares, so the axis the day leans on hardest is said first.
    takes: party ? asked(party.activity).map(([axis]) => TAKES[axis]) : [],
    /**
     * Their own witness first (v0.3.2): what this person was under, saw,
     * heard, or was told, strongest experience leading — the miracle they
     * stood in stays at the top of their story for as long as they carry it.
     * A person who has witnessed nothing yet falls back to what the band
     * remembers, offered whole — most band memory is herd movement the
     * Chronicle stays silent about, so any shortlist here reads as amnesia
     * — newest first, with no grade, because communal memory has none.
     */
    remembers: person.memories.length > 0
      ? [...person.memories]
          .sort((a, b) => b.importance - a.importance || b.time - a.time)
          .map((m) => ({
            eventId: m.eventId, day: Math.floor(m.time / DAY),
            how: m.from ? heardFrom(m.from, culture?.name, peopleOf) : HOW[m.kind],
          }))
      : [...band.memories]
          .sort((a, b) => b.time - a.time)
          .map((m) => ({ eventId: m.eventId, day: Math.floor(m.time / DAY) })),
  };
}
